// Configuración → Operación (client-ops §3): how the workspace hands work out.
//
// ONE card, one question, since CP3 L. A política de asignación is a NAMED
// ENTITY (Juan's 2026-08-19 lock) — a name, an ordered rule list (first match
// wins) and an EXPLICIT fallback — that the tenant creates here and attaches to
// the stages they choose in Procesos. The manager is ALWAYS visible: there is no
// mode select hiding it any more, because there is no workspace default any
// more. The fallback defaults to 'Sin asignar' on purpose — the old silent
// balanced fallback assigning non-matching leads is the confusion this screen
// exists to end.
//
// The conversation-visibility wall used to be a second card here and READ WRONG:
// nothing on this tab said it was about Bandeja. It now lives in Equipo
// (ConversationVisibilityCard), where the audience it governs is on screen. Its
// API did not move.
//
// Manage-only, like the tab that mounts it. The API refuses an Asesor anyway;
// the tab simply isn't offered to them.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { type FieldDefinition, listFields } from './fields-data.js'
import {
  type AssignableMemberView,
  type AssignmentPoliciesSettings,
  type AssignmentPolicyView,
  createAssignmentPolicy,
  deleteAssignmentPolicy,
  getAssignmentPolicies,
  type PolicyFallback,
  updateAssignmentPolicy,
} from './settings-data.js'
import {
  Button,
  FormField,
  Input,
  Modal,
  RadioGroup,
  Select,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'

/** Qué pasa cuando ninguna regla aplica, dicho como consecuencia. 'Sin
 *  asignar' va primero y recomendado: el viejo respaldo equilibrado silencioso
 *  asignaba prospectos que ninguna regla reclamó, y nadie sabía por qué. */
const FALLBACK_OPTIONS: { value: PolicyFallback; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'Sin asignar (recomendado)',
    description: 'El prospecto queda visible sin responsable y alguien lo toma manualmente.',
  },
  {
    value: 'balanced',
    label: 'Equilibrada',
    description: 'Se asigna a quien tenga menos trabajo activo.',
  },
]

const FALLBACK_LABEL: Record<PolicyFallback, string> = {
  none: 'Sin asignar',
  balanced: 'Equilibrada',
}

/** A rule row while it is being edited: `memberId` may still be empty. */
interface DraftRule {
  field: string
  equals: string
  memberId: string
}

/** The policy modal's draft. Null target = creating a new one. */
interface PolicyDraft {
  id: string | null
  name: string
  rules: DraftRule[]
  fallback: PolicyFallback
}

function draftOf(p: AssignmentPolicyView): PolicyDraft {
  return {
    id: p.id,
    name: p.name,
    rules: p.rules.map((r) => ({ field: r.field, equals: r.equals, memberId: r.memberId })),
    fallback: p.fallback,
  }
}

/** "3 reglas · Si ninguna aplica: Sin asignar · En uso en 2 etapas" — the one
 *  line that lets the list be read without opening every modal. */
function policyMeta(p: AssignmentPolicyView): string {
  const rules = `${p.rules.length} ${p.rules.length === 1 ? 'regla' : 'reglas'}`
  const fallback = `Si ninguna aplica: ${FALLBACK_LABEL[p.fallback]}`
  const inUse =
    p.inUseStageCount === 0
      ? 'Sin etapas'
      : `En uso en ${p.inUseStageCount} ${p.inUseStageCount === 1 ? 'etapa' : 'etapas'}`
  return `${rules} · ${fallback} · ${inUse}`
}

export function OperationsSection(): JSX.Element {
  const qc = useQueryClient()
  const policiesQuery = useQuery({
    queryKey: ['assignment-policies'],
    queryFn: getAssignmentPolicies,
  })
  // El catálogo de campos de prospecto: lo que una regla puede comparar. Antes
  // "Campo" era texto libre, así que una regla podía apuntar a una clave que no
  // existe en ningún lado y no disparar nunca sin decir por qué.
  const leadFieldsQuery = useQuery({
    queryKey: ['fields', 'lead'],
    queryFn: () => listFields('lead'),
  })

  const [draft, setDraft] = useState<PolicyDraft | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AssignmentPolicyView | null>(null)
  const [busy, setBusy] = useState(false)

  if (policiesQuery.isError) {
    return (
      <QueryError
        message="No pudimos cargar la configuración de operación."
        onRetry={() => void policiesQuery.refetch()}
      />
    )
  }
  if (policiesQuery.isLoading || !policiesQuery.data) {
    return <Spinner label="Cargando operación…" />
  }

  const policies = policiesQuery.data.policies
  const members: AssignableMemberView[] = policiesQuery.data.members
  const memberOptions = members.map((m) => ({ value: m.id, label: m.name }))
  // Solo los campos ACTIVOS: una regla nueva no puede apuntar a un campo que el
  // espacio ocultó. Una regla vieja que sí lo hace se sigue viendo (abajo).
  const leadFields: FieldDefinition[] = (leadFieldsQuery.data ?? []).filter((f) => f.active)
  const fieldOptions = leadFields.map((f) => ({ value: f.key, label: f.label }))

  // El catálogo tarda lo suyo, y un selector vacío mientras llega se lee como
  // "no tienes campos" en vez de "todavía no sé". Se dice.
  const fieldsLoading = leadFieldsQuery.isLoading || leadFieldsQuery.data === undefined

  /** Las opciones del selector Campo de UNA regla: el catálogo, más la clave que
   *  esa regla ya tiene guardada cuando el catálogo no la conoce. Una regla
   *  escrita hace meses no se borra sola por un cambio de campos: se ve, se
   *  marca, y quien la escribió decide. */
  function fieldOptionsFor(field: string): { value: string; label: string }[] {
    if (field === '' || leadFields.some((f) => f.key === field)) return fieldOptions
    return [...fieldOptions, { value: field, label: `${field} (campo desconocido)` }]
  }

  function patchRule(index: number, patch: Partial<DraftRule>): void {
    setDraft((prev) =>
      prev
        ? { ...prev, rules: prev.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) }
        : prev,
    )
  }

  function addRule(): void {
    setDraft((prev) =>
      prev ? { ...prev, rules: [...prev.rules, { field: '', equals: '', memberId: '' }] } : prev,
    )
  }

  function removeRule(index: number): void {
    setDraft((prev) => (prev ? { ...prev, rules: prev.rules.filter((_, i) => i !== index) } : prev))
  }

  /** Run a policy mutation, seed the cache with the list it returned and toast.
   *  The API's own sentence wins on failure: every refusal already names the
   *  rule that stopped you, in Spanish. */
  async function run(
    fn: () => Promise<AssignmentPoliciesSettings>,
    okMsg: string,
  ): Promise<boolean> {
    setBusy(true)
    try {
      const next = await fn()
      qc.setQueryData(['assignment-policies'], next)
      // The stage modal in Procesos and the board chips read the same list.
      await qc.invalidateQueries({ queryKey: ['settings-pipelines'] })
      await qc.invalidateQueries({ queryKey: ['leads'] })
      toast.ok(okMsg)
      return true
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos guardar el cambio.'))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function submitPolicy(): Promise<void> {
    if (!draft) return
    const name = draft.name.trim()
    if (name === '') {
      toast.error('Escribe un nombre para la política.')
      return
    }
    // Incomplete rows are a half-typed thought, not a rule. Refusing here (with
    // the reason) beats sending them and letting the API answer in numbers.
    const incomplete = draft.rules.findIndex(
      (r) => r.field.trim() === '' || r.equals.trim() === '' || r.memberId === '',
    )
    if (incomplete >= 0) {
      toast.error(`Completa la regla ${incomplete + 1} antes de guardar.`)
      return
    }
    const edit = {
      name,
      rules: draft.rules.map((r) => ({
        field: r.field.trim(),
        equals: r.equals.trim(),
        memberId: r.memberId,
      })),
      fallback: draft.fallback,
    }
    const ok = await run(
      () =>
        draft.id === null ? createAssignmentPolicy(edit) : updateAssignmentPolicy(draft.id, edit),
      draft.id === null ? 'Política creada.' : 'Guardamos la política.',
    )
    if (ok) setDraft(null)
  }

  async function submitDelete(): Promise<void> {
    if (!deleteTarget) return
    if (await run(() => deleteAssignmentPolicy(deleteTarget.id), 'Política eliminada.')) {
      setDeleteTarget(null)
    }
  }

  /** Por qué no se puede eliminar, en las palabras del confirm — o null cuando
   *  sí se puede. El mismo número que el 409 del API citaría (guard inside the
   *  confirm, patrón de #751). */
  const deleteBlocker =
    deleteTarget && deleteTarget.inUseStageCount > 0
      ? `Esta política está asignada a ${deleteTarget.inUseStageCount} ${
          deleteTarget.inUseStageCount === 1 ? 'etapa' : 'etapas'
        }. Quítala de esas etapas antes de eliminarla.`
      : null

  return (
    <section data-testid="operations-section" className="flex flex-col gap-5">
      <div className="rounded-xl border border-line bg-surface p-5">
        <h2 className="text-[13px] font-semibold text-text">Políticas de asignación</h2>
        <p className="mt-1 text-[12px] text-text-2">
          Crea políticas con nombre y asígnalas a las etapas que quieras en Procesos.
        </p>
        {/* Qué significa "asignar", una sola vez y en el lugar donde se decide.
            Sin esto, las políticas parecen una función aparte en lugar de una
            manera de responder la misma pregunta de siempre. */}
        <p className="mt-3 text-[11.5px] leading-relaxed text-text-3">
          Asignar es dar responsabilidad. El prospecto aparece en el tablero al filtrar Asignado a
          por esa persona, se le notifica, cuenta como parte de su carga de trabajo y define lo que
          ve cuando la visibilidad es Cada quien lo suyo. Manual o automática significan lo mismo
          para el prospecto, lo único que cambia es quién eligió.
        </p>

        <div className="mt-4 flex flex-col gap-2" data-testid="assignment-policies">
          {policies.length === 0 && (
            <p className="text-[12px] text-text-2">
              Todavía no tienes políticas. Crea una y asígnala a una etapa en Procesos.
            </p>
          )}
          {policies.map((p) => (
            <div
              key={p.id}
              data-testid={`policy-row-${p.id}`}
              className="grid grid-cols-[minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-lg border border-line p-3 sm:grid-cols-[minmax(0,1fr)_auto]"
            >
              <div className="min-w-0">
                <p className="truncate text-[12.5px] font-semibold text-text">{p.name}</p>
                <p className="mt-0.5 truncate text-[11.5px] text-text-2">{policyMeta(p)}</p>
              </div>
              <div className="flex flex-none items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  aria-label={`Editar ${p.name}`}
                  onClick={() => setDraft(draftOf(p))}
                >
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  aria-label={`Eliminar ${p.name}`}
                  onClick={() => setDeleteTarget(p)}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setDraft({ id: null, name: '', rules: [], fallback: 'none' })}
          >
            Nueva política
          </Button>
        </div>
      </div>

      {/* Crear / editar política */}
      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id === null ? 'Nueva política' : 'Editar política'}
        subtitle="Las reglas se leen en orden y gana la primera que aplique."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitPolicy()}
            >
              {busy ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-4">
            <FormField label="Nombre" htmlFor="policy-name">
              <Input
                id="policy-name"
                placeholder="Ventas Norte"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </FormField>

            <div className="flex flex-col gap-2" data-testid="policy-rules">
              {members.length === 0 && (
                <p className="text-[12px] text-text-2">
                  Todavía no hay asesores a quienes asignar. Invita a alguien en la pestaña Equipo.
                </p>
              )}
              {draft.rules.length > 0 && (
                // Encabezados UNA vez, filas alineadas debajo: la misma plantilla
                // de columnas en cada fila (la disciplina del filtro del
                // directorio admin), no un flex que se acomoda distinto por fila.
                <div className="hidden gap-3 text-[11px] font-medium text-text-2 sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_60px]">
                  <span>Campo</span>
                  <span>Valor</span>
                  <span>Asesor</span>
                  <span aria-hidden="true" />
                </div>
              )}
              {draft.rules.map((rule, index) => {
                const def = leadFields.find((f) => f.key === rule.field)
                // El valor HISTÓRICO de la regla (D21-2): desde que las opciones
                // de un dato pueden venir de una fuente viva (D21-1), la lista
                // cambia sin que nadie edite la política. Si el Select no
                // ofreciera lo que la regla ya compara, la casilla se vería
                // VACÍA y el siguiente guardado borraría una regla que sigue
                // funcionando. Se ofrece y se avisa; no se bloquea el guardado.
                const historical =
                  def?.data_type === 'enum' &&
                  rule.equals !== '' &&
                  !(def.options ?? []).includes(rule.equals)
                    ? rule.equals
                    : null
                return (
                  <div
                    key={index}
                    className="grid grid-cols-1 items-center gap-3 rounded-lg border border-line p-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_60px] sm:border-0 sm:p-0"
                  >
                    {/* El catálogo del espacio, por su NOMBRE; lo que se guarda es
                      la clave. Es el mismo vocabulario que captura el Agente y
                      que requieren las etapas: una regla ya no puede comparar
                      contra un campo que no existe. */}
                    <Select
                      aria-label={`Campo de la regla ${index + 1}`}
                      placeholder={fieldsLoading ? 'Cargando campos…' : 'Elige un campo…'}
                      disabled={fieldsLoading}
                      options={fieldOptionsFor(rule.field)}
                      value={rule.field}
                      onChange={(e) =>
                        // Cambiar de campo invalida el valor: la lista de opciones
                        // del anterior no dice nada del nuevo.
                        patchRule(index, { field: e.target.value, equals: '' })
                      }
                    />
                    {def?.data_type === 'enum' ? (
                      <Select
                        aria-label={`Valor de la regla ${index + 1}`}
                        placeholder="Elige un valor…"
                        options={[
                          ...(historical !== null
                            ? [{ value: historical, label: historical }]
                            : []),
                          ...(def.options ?? []).map((o) => ({ value: o, label: o })),
                        ]}
                        value={rule.equals}
                        onChange={(e) => patchRule(index, { equals: e.target.value })}
                      />
                    ) : (
                      <Input
                        aria-label={`Valor de la regla ${index + 1}`}
                        placeholder="Monterrey"
                        value={rule.equals}
                        onChange={(e) => patchRule(index, { equals: e.target.value })}
                      />
                    )}
                    <Select
                      aria-label={`Asesor de la regla ${index + 1}`}
                      options={memberOptions}
                      placeholder="Elige a una persona…"
                      value={rule.memberId}
                      onChange={(e) => patchRule(index, { memberId: e.target.value })}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRule(index)}
                      aria-label={`Quitar la regla ${index + 1}`}
                    >
                      Quitar
                    </Button>
                    {historical !== null && (
                      <p className="text-[11.5px] text-text-2 sm:col-span-4">
                        Este valor ya no está en la lista de opciones del dato. La regla sigue
                        aplicando a los prospectos que lo tengan.
                      </p>
                    )}
                  </div>
                )
              })}
              <div>
                <Button variant="secondary" onClick={addRule} disabled={members.length === 0}>
                  Agregar regla
                </Button>
              </div>
              {/* El selector Campo ofrece TODA la biblioteca del espacio, no la
                  ficha de un proceso: una política es cruzada, se puede
                  atachar a etapas de procesos distintos, y no hay un proceso
                  al cual acotarla. La consecuencia se dice en vez de
                  esconderse (CP3 K). */}
              <p className="text-[11px] leading-relaxed text-text-3" data-testid="rules-scope-hint">
                Las reglas comparan los datos que cada proceso captura. Si un proceso no lleva el
                dato de una regla, esa regla nunca aplica ahí.
              </p>
            </div>

            <RadioGroup
              label="Si ninguna regla aplica:"
              name="policy-fallback"
              value={draft.fallback}
              options={FALLBACK_OPTIONS}
              onChange={(v) => setDraft({ ...draft, fallback: v })}
            />
          </div>
        )}
      </Modal>

      {/* Eliminar política — o la regla que lo impide, dicha aquí y no bajo la
          fila, donde sería ruido permanente. */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        role="alertdialog"
        title="Eliminar política"
        subtitle={deleteTarget ? deleteTarget.name : ''}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeleteTarget(null)}
              disabled={busy}
            >
              {deleteBlocker === null ? 'Cancelar' : 'Entendido'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={busy || deleteBlocker !== null}
              onClick={() => void submitDelete()}
            >
              Eliminar política
            </Button>
          </>
        }
      >
        {deleteBlocker !== null ? (
          <p
            className="text-[12.5px] leading-relaxed text-text-2"
            data-testid="policy-delete-blocker"
          >
            {deleteBlocker}
          </p>
        ) : (
          <p className="text-[12.5px] leading-relaxed text-text-2">
            {deleteTarget
              ? `Se elimina "${deleteTarget.name}" y sus reglas. Esta acción no se puede deshacer.`
              : ''}
          </p>
        )}
      </Modal>
    </section>
  )
}
